import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy } from "lucide-react";
import { copyToClipboard } from "@/i18n/copyable-reference";
import { cn } from "@/lib/utils";

/**
 * Design-system identifier display (ALL-4921, ALL-5792): transfer intent ids,
 * wallet ids, on-chain addresses, tx hashes and other long opaque identifiers.
 *
 * - The string itself is shortened (prefix…suffix), not only clipped with CSS,
 *   so a long value can never push a fixed-width column past its card edge.
 * - The full value is always reachable: on hover and on keyboard focus (a
 *   tooltip), on the title attribute, and through the copy button. Nothing is
 *   dropped; this is a presentation transform.
 * - Copy is one click or one Enter/Space, with a visible "Copied" check and a
 *   polite live-region announcement. A refused clipboard write never claims
 *   success: the live region says it could not copy and the value stays
 *   selectable.
 * - `kind` picks a readable short form and an optional type label: an intent
 *   id keeps its `gti_` prefix ("gti_9f3c…a1b2"), an address or hash keeps
 *   `0x` plus four ("0x1a2b…c3d4").
 */

const COPIED_RESET_MS = 2000;

export type IdentifierKind = "intent" | "wallet" | "address" | "tx" | "draft" | "approval" | "id";

const KIND_LABEL: Record<IdentifierKind, string> = {
  intent: "Intent",
  wallet: "Wallet",
  address: "Address",
  tx: "Tx",
  draft: "Draft",
  approval: "Approval",
  id: "ID",
};

const KIND_NOUN: Record<IdentifierKind, string> = {
  intent: "intent ID",
  wallet: "wallet ID",
  address: "address",
  tx: "transaction hash",
  draft: "draft ID",
  approval: "approval ID",
  id: "ID",
};

function middleEllipsis(value: string, head = 8, tail = 6): string {
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

/** The readable short form for a kind. Without a kind the generic 8…6 form is kept. */
export function shortIdentifier(value: string, kind?: IdentifierKind): string {
  if (!kind) return middleEllipsis(value);
  if (/^0x[0-9a-fA-F]+$/.test(value)) return middleEllipsis(value, 6, 4);
  const prefix = /^[A-Za-z]{2,10}[_-]/.exec(value);
  if (prefix) return middleEllipsis(value, prefix[0].length + 4, 4);
  return middleEllipsis(value, 6, 4);
}

type CopyState = "idle" | "copied" | "failed";

export function IdentifierValue({
  value,
  kind,
  label,
  href,
  copy = true,
  emptyFallback = "—",
  className,
}: {
  value: string | null | undefined;
  /** What the identifier is. Drives the short form, the type label and the copy button's name. */
  kind?: IdentifierKind;
  /**
   * Show the type label ("Intent") before the value. On by default when `kind` is set;
   * a table whose column header already names the identifier passes false.
   */
  label?: boolean;
  /** Makes the value a link (for example to a detail page). Copy stays a separate button. */
  href?: string;
  copy?: boolean;
  emptyFallback?: string;
  className?: string;
}) {
  const [state, setState] = useState<CopyState>("idle");
  // The full-value tooltip is rendered only while the value is hovered or focused, so the
  // full string is not duplicated into the page text the rest of the time.
  const [tipOpen, setTipOpen] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tipId = useId();

  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
  }, []);

  if (!value || !value.trim()) {
    return <span className={className}>{emptyFallback}</span>;
  }

  const full = value.trim();
  const short = shortIdentifier(full, kind);
  const showLabel = (label ?? Boolean(kind)) && Boolean(kind);
  const noun = kind ? KIND_NOUN[kind] : undefined;

  async function handleCopy() {
    const ok = await copyToClipboard(full);
    setState(ok ? "copied" : "failed");
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setState("idle"), COPIED_RESET_MS);
  }

  const valueNode = href ? (
    <Link
      to={href}
      aria-describedby={tipOpen ? tipId : undefined}
      className="min-w-0 truncate font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
    >
      <code dir="ltr" title={full} className="font-mono">{short}</code>
    </Link>
  ) : (
    // tabIndex makes the value focusable, so a keyboard user reaches the full-value tooltip too.
    <code
      className="min-w-0 truncate rounded-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
      title={full}
      dir="ltr"
      tabIndex={0}
      aria-describedby={tipOpen ? tipId : undefined}
    >
      {short}
    </code>
  );

  return (
    <span
      className={cn("group/ident relative inline-flex min-w-0 max-w-full items-center justify-end gap-1.5", className)}
      data-identifier-kind={kind}
      onMouseEnter={() => setTipOpen(true)}
      onMouseLeave={() => setTipOpen(false)}
      onFocus={() => setTipOpen(true)}
      onBlur={() => setTipOpen(false)}
    >
      {showLabel && kind && (
        <span className="shrink-0 rounded bg-bg px-1.5 py-px text-[10.5px] font-semibold uppercase tracking-wide text-muted">
          {KIND_LABEL[kind]}
        </span>
      )}
      {valueNode}
      {tipOpen && (
        <span
          id={tipId}
          role="tooltip"
          className="pointer-events-none absolute right-0 top-full z-50 mt-1.5 w-max max-w-[22rem] break-all rounded-md bg-ink px-2 py-1 text-left font-mono text-[11px] font-medium text-white shadow"
        >
          {full}
        </span>
      )}
      {copy && (
        <button
          type="button"
          onClick={() => void handleCopy()}
          aria-label={noun ? `Copy ${noun} ${full}` : `Copy ${full}`}
          title="Copy"
          data-copied={state === "copied" ? "true" : undefined}
          className={cn(
            "inline-flex min-h-7 min-w-7 shrink-0 items-center justify-center gap-1 rounded-md px-1 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
            state === "copied" ? "text-success" : "text-muted hover:bg-bg hover:text-primary",
          )}
        >
          {state === "copied" ? (
            <>
              <Check aria-hidden className="size-3.5" />
              <span>Copied</span>
            </>
          ) : (
            <>
              <Copy aria-hidden className="size-3.5" />
              <span className="sr-only">Copy</span>
            </>
          )}
        </button>
      )}
      <span className="sr-only" aria-live="polite">
        {state === "copied" ? "Copied" : state === "failed" ? "Couldn't copy. Select the value to copy it." : ""}
      </span>
    </span>
  );
}
